import { Button } from '@mui/material';
import type { GridRowsProp } from '@mui/x-data-grid';
import { VyIcon } from '@vy/ui-design-system';
import { isRevShareProductVariant, isTieredSubscriptionProductVariant } from '@vy/services';
import { MAX_TO, type PriceTier } from '@vy/types';
import { isNotNullOrUndefined, withDefault } from '@vy/utils';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { addNewTier, adjustTiers } from '../../../../../../../utils/tiers-modifier';
import type { PlanInputForm } from '../../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { getEditTierTableColumns } from './edit-tiers-table-columns';
import { StyledUsageProductVariantTableEdit } from './styled-usage-product-variant-table-edit';
import type { UsageProductVariantTableRow } from './usage-product-variant-table-edit-types/usage-product-variant-table-row';

type UsageProductVariantTableEditProps = {
  tiers: Partial<PriceTier>[];
  productIndex: number;
  productVariantIndex: number;
  isFlatRate?: boolean;
  isChunked?: boolean;
};

export const UsageProductVariantTableEdit: React.FC<UsageProductVariantTableEditProps> = ({
  tiers,
  productIndex,
  productVariantIndex,
  isFlatRate = false,
  isChunked = false,
}) => {
  const { setValue, getValues } = useFormContext<PlanInputForm>();

  const variant = getValues(`usageProducts.${productIndex}.variants.${productVariantIndex}`);

  const isRevShareVariant = isRevShareProductVariant(variant);
  const isTieredSubscriptionVariant = isTieredSubscriptionProductVariant(variant);

  const addRow = useCallback(() => {
    const currentTiers = getValues(
      `usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers`,
    );
    const updatedTiers = addNewTier(currentTiers);
    setValue(
      `usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers`,
      updatedTiers,
    );
  }, [getValues, productIndex, productVariantIndex, setValue, addNewTier]);

  const isFromCell = (field: string) => field === 'from';

  const setTierValueByIndex = (index: number, value: Partial<PriceTier>) => {
    setValue(
      `usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers.${index}`,
      value ?? MAX_TO,
    );
  };

  const handleRowUpdate = useCallback(
    ({ id, index, ...priceTier }: UsageProductVariantTableRow, oldRow: UsageProductVariantTableRow) => {
      if (!isPriceTierValid(priceTier)) {
        const to = isNotNullOrUndefined(priceTier.from) ? priceTier.from : oldRow.to;

        if (isNotNullOrUndefined(priceTier.price)) {
          setValue(
            `usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers.${index}.price`,
            priceTier.price,
          );

          return {
            ...oldRow,
            to,
            price: priceTier.price,
          };
        }

        return {
          ...oldRow,
          to,
        };
      }

      setValue(
        `usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers.${index}`,
        priceTier,
      );
      const currentTiers = getValues(
        `usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers`,
      );
      const adjustedTiers = adjustTiers(currentTiers);

      setValue(
        `usageProducts.${productIndex}.variants.${productVariantIndex}.pricing.priceConfiguration.tiers`,
        adjustedTiers,
      );

      return {
        ...oldRow,
        ...priceTier,
      };
    },
    [getValues, productIndex, productVariantIndex, setValue],
  );

  return (
    <div className="h-full w-full space-y-2 px-6 py-5">
      <div className="mx-1">
        <StyledUsageProductVariantTableEdit
          columns={getEditTierTableColumns(
            isRevShareVariant,
            isTieredSubscriptionVariant,
            isFlatRate,
            isChunked,
            setTierValueByIndex,
          )}
          rows={buildRows(tiers, productIndex, productVariantIndex)}
          hideFooter
          disableColumnMenu
          showCellVerticalBorder
          editMode="cell"
          autoHeight
          isCellEditable={({ field }) => !isFromCell(field)}
          processRowUpdate={handleRowUpdate}
          rowSelection={false}
        />
      </div>

      <div className="w-full text-end">
        <Button
          onClick={addRow}
          variant="text"
          sx={{
            columnGap: '8px',
          }}
        >
          <VyIcon
            icon="PlusMedium"
            style={{
              transform: 'scale(0.75)',
            }}
          />
          <span>Add Row</span>
        </Button>
      </div>
    </div>
  );
};

function isPriceTierValid(tier: Partial<PriceTier>): boolean {
  const from = withDefault(tier?.from, 0);
  const to = withDefault(tier?.to, 0);

  return from <= to;
}

function buildRows(
  tiers: Partial<PriceTier>[],
  productIndex: number,
  productVariantIndex: number,
): GridRowsProp<UsageProductVariantTableRow> {
  return tiers.map((tier, index) => ({
    ...tier,
    id: index.toString(),
    index,
    productIndex,
    productVariantIndex,
  }));
}
